import { ISendInfo, ISendRecipientInfo, SendCorrespondenceType } from '@alberta/konexi-shared';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { UserNamePipe } from 'src/app/common/pipes/user/username.pipe';

import { toSendDate } from './latest-send-info';

/**
 * Renders the send information of one dispatch — correspondence type, sender, recipients
 * and send timestamp — uniformly for documents and attachments.
 *
 * Deliberately renders only the text block and not the surrounding `itl-detail-item`: the
 * separator line between detail items comes from the adjacent sibling selector
 * `itl-detail-item + :host` in `detail-item.component.scss`, and an extra wrapper element
 * would break it for this item and the following one.
 *
 * Usage:
 * ```html
 * <itl-detail-item *ngIf="latestSend">
 *   <itl-detail-item-icon><ion-icon [name]="latestSend.icon"></ion-icon></itl-detail-item-icon>
 *   <itl-send-info
 *     [info]="latestSend.info"
 *     [correspondenceType]="latestSend.correspondenceType"
 *     [senderIsUserId]="latestSend.senderIsUserId"
 *   ></itl-send-info>
 * </itl-detail-item>
 * ```
 */
@Component({
  selector: 'itl-send-info',
  templateUrl: './send-info.component.html',
  styleUrls: ['./send-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TranslateModule, UserNamePipe],
})
export class SendInfoComponent {
  /** Send information. Without it the component renders nothing. */
  @Input() public info: ISendInfo | null | undefined;

  /**
   * Correspondence type; drives the label and which recipient address becomes the last line.
   *
   * Required on purpose: every call site gets it from {@link latestSendInfo}, and a default would
   * silently label a KIM dispatch as "Via Fax" if a binding were ever forgotten.
   */
  @Input({ required: true }) public correspondenceType!: SendCorrespondenceType;

  /**
   * Whether `info.sentBy` is a user id that has to be resolved through the `username` pipe.
   *
   * Existing data in `audit.emailInfo` holds the sender's e-mail address instead, which the
   * pipe would turn into "Unbekannter Benutzer".
   */
  @Input() public senderIsUserId = true;

  /** i18n key of the correspondence type label. */
  public get correspondenceTypeKey(): string {
    return `sendInfo.types.${this.correspondenceType}`;
  }

  public get recipients(): ISendRecipientInfo[] {
    return this.info?.recipients ?? [];
  }

  /**
   * Send timestamp as a `Date`, or `null` when it is absent or unusable.
   *
   * The template must bind the date pipes to this rather than to `info.sentAt`: that field is
   * typed `Date | string | null`, and Angular's `DatePipe` throws on a string it cannot parse,
   * which would take down the whole detail page instead of just omitting the line.
   */
  public get sentAtDate(): Date | null {
    return toSendDate(this.info?.sentAt);
  }

  /**
   * Display lines of one recipient: the address, followed by the address of the respective
   * correspondence type as the last line.
   *
   * Existing data holds the address as a single pre-formatted line (`postalAddress`), while
   * new entries keep name, street and city apart. Empty parts are dropped so a manually
   * entered fax number leaves nothing but the number.
   *
   * Postal addresses written before this feature occasionally contain the literal string
   * "undefined"; those are discarded so the display falls back to the recipient name.
   */
  public recipientLines(recipient: ISendRecipientInfo | null | undefined): string[] {
    if (!recipient) {
      return [];
    }

    const postalAddress =
      recipient.postalAddress && !recipient.postalAddress.includes('undefined') ? recipient.postalAddress : null;
    const address = postalAddress ? [postalAddress] : [recipient.name, recipient.street, recipient.city];

    return [...address, this.channelAddress(recipient)].filter(
      (line): line is string => !!line && line.trim().length > 0
    );
  }

  private channelAddress(recipient: ISendRecipientInfo): string | null | undefined {
    switch (this.correspondenceType) {
      case 'kim':
        return recipient.kimAddress;
      case 'email':
        return recipient.email;
      default:
        return recipient.fax;
    }
  }
}
